import React, { useEffect, useMemo } from "react";
import {
	alpha,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import ConfirmationNumberTwoToneIcon from "@mui/icons-material/ConfirmationNumberTwoTone";
import PersonTwoToneIcon from "@mui/icons-material/PersonTwoTone";
import PaymentTwoToneIcon from "@mui/icons-material/PaymentTwoTone";
import NoteAltTwoToneIcon from "@mui/icons-material/NoteAltTwoTone";
import PaidTwoToneIcon from "@mui/icons-material/PaidTwoTone";
import { useForm, Controller } from "react-hook-form";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import type { IVisitorTicket } from "../type/newVisitorType";
import type {
	ITicketListItem,
	IUpdateTicketPayload,
} from "../type/ticketScanType";

interface Props {
	open: boolean;
	handleClose: () => void;
	ticket: ITicketListItem | IVisitorTicket | any | null;
}


interface FormValues {
	date: string;
	quantity: number | string;
	discount: number | string;
	payment_status: string;
	payment_method: string;
	note: string;
}

export interface IFairCommon {
	fair_id: number;
	fair_name: string;
	fair_start_date: string;
	fair_end_date: string;
	fair_venue_layout?: string | null;
}

export interface ITicketDateItem {
	value: string;
	label: string;
	day: string;
	weekday: string;
}

export const generateTicketDates = (
	startDateStr?: string | null,
	endDateStr?: string | null
): ITicketDateItem[] => {
	if (!startDateStr || !endDateStr) return [];

	const [sYear, sMonth, sDay] = startDateStr.split("-").map(Number);
	const [eYear, eMonth, eDay] = endDateStr.split("-").map(Number);

	if (!sYear || !sMonth || !sDay || !eYear || !eMonth || !eDay) return [];

	const start = new Date(sYear, sMonth - 1, sDay);
	const end = new Date(eYear, eMonth - 1, eDay);

	if (start > end) return [];

	const MONTH_NAMES = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];
	const WEEKDAYS = [
		"Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"
	];

	const dates: ITicketDateItem[] = [];
	const current = new Date(start);
	let dayIndex = 1;

	while (current <= end) {
		const y = current.getFullYear();
		const m = String(current.getMonth() + 1).padStart(2, "0");
		const d = String(current.getDate()).padStart(2, "0");
		const val = `${y}-${m}-${d}`;
		const lbl = `${current.getDate()} ${MONTH_NAMES[current.getMonth()]}, ${y}`;
		const wk = WEEKDAYS[current.getDay()];

		dates.push({
			value: val,
			label: lbl,
			day: `Day ${dayIndex}`,
			weekday: wk,
		});

		current.setDate(current.getDate() + 1);
		dayIndex++;
	}

	return dates;
};

const getDefaultTicketDate = (dates?: ITicketDateItem[]): string => {
	if (!dates || dates.length === 0) return "";
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	const todayStr = `${year}-${month}-${day}`;

	const matched = dates.find((item) => item.value === todayStr);
	return matched ? matched.value : (dates[0]?.value || "");
};

const getGridXs = (total: number) => {
	if (total <= 1) return { xs: 12 };
	if (total === 2) return { xs: 6 };
	if (total === 3) return { xs: 4 };
	if (total === 4) return { xs: 6, sm: 3 };
	return { xs: 6, sm: 4, md: 3 };
};

const PAYMENT_METHODS = [
	{ value: "online", label: "Online" },
	{ value: "cash", label: "Cash" },
];

const PAYMENT_STATUSES = [
	{ value: "paid", label: "Paid" },
	{ value: "unpaid", label: "Unpaid" },
	{ value: "free", label: "Free" },
];

const UpdateTicketModal: React.FC<Props> = ({ open, handleClose, ticket }) => {
	const theme = useTheme();

	// Fetch fair dates from /api/common/fair-list
	const { data: rawFair, isLoading: isFairLoading } = useApiQuery<IFairCommon | IFairCommon[]>({
		endPoint: "/api/common/fair-list",
		queryKeys: ["CommonFairList"],
	});

	const fairData: IFairCommon | null = rawFair
		? Array.isArray(rawFair)
			? rawFair[0] || null
			: (rawFair as any)?.data && typeof (rawFair as any).data === "object"
				? Array.isArray((rawFair as any).data)
					? (rawFair as any).data[0] || null
					: (rawFair as any).data
				: rawFair
		: null;

	const ticketDates = useMemo(() => {
		return generateTicketDates(fairData?.fair_start_date, fairData?.fair_end_date);
	}, [fairData?.fair_start_date, fairData?.fair_end_date]);

	const {
		register,
		control,
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			date: "",
			quantity: 1,
			discount: 0,
			payment_status: "paid",
			payment_method: "online",
			note: "",
		},
	});

	const watchedDate = watch("date") || getDefaultTicketDate(ticketDates);

	useEffect(() => {
		if (ticket && open) {
			let noteVal = "";
			if (ticket.payment_details) {
				if (typeof ticket.payment_details === "object") {
					noteVal =
						ticket.payment_details.note ||
						ticket.payment_details.trxID ||
						ticket.payment_details.trx_id ||
						"";
				} else if (typeof ticket.payment_details === "string") {
					try {
						const parsed = JSON.parse(ticket.payment_details);
						noteVal = parsed.note || parsed.trxID || ticket.payment_details;
					} catch {
						noteVal = ticket.payment_details;
					}
				}
			}

			reset({
				date: ticket.date || getDefaultTicketDate(ticketDates),
				quantity: ticket.quantity || 1,
				discount: ticket.discount || 0,
				payment_status: (ticket.payment_status || "paid").toLowerCase(),
				payment_method: (ticket.payment_method || "online").toLowerCase(),
				note: noteVal,
			});
		}
	}, [ticket, open, reset, ticketDates]);

	// Synchronize date once dynamic fair dates load if no date is currently set
	useEffect(() => {
		if (ticketDates.length > 0 && !watch("date")) {
			const defaultDate = ticket?.date || getDefaultTicketDate(ticketDates);
			if (defaultDate) {
				setValue("date", defaultDate, { shouldValidate: true });
			}
		}
	}, [ticketDates, ticket?.date, setValue, watch]);

	const { mutate: updateTicket, isPending } = useApiMutation<
		IUpdateTicketPayload,
		any
	>({
		method: "PATCH",
		endPoint: `/api/v1/admin/visitor/ticket/${ticket?.id}`,
		invalidateQueries: [
			"AllVisitorTickets",
			"TicketScans",
			"VisitorDetails",
		],
	});

	const onSubmit = (formData: FormValues) => {
		if (!ticket?.id) return;

		const payload: IUpdateTicketPayload = {
			date: formData.date || watchedDate || getDefaultTicketDate(ticketDates),
			quantity: Math.max(1, Number(formData.quantity) || 1),
			discount: Math.max(0, Number(formData.discount) || 0),
			payment_status: formData.payment_status,
			payment_method: formData.payment_method,
			payment_details: {
				...(typeof ticket.payment_details === "object"
					? ticket.payment_details
					: {}),
				note: formData.note?.trim() || undefined,
			},
		};

		updateTicket(payload, {
			onSuccess: () => {
				handleClose();
			},
		});
	};

	const onDialogClose = () => {
		if (isPending) return;
		handleClose();
	};

	if (!ticket) return null;

	return (
		<Dialog
			open={open}
			onClose={onDialogClose}
			maxWidth="sm"
			fullWidth
			disableEnforceFocus
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<EditTwoToneIcon color="primary" />
					<Typography variant="h4" fontWeight={600}>
						Update Ticket
					</Typography>
					<Chip
						label={ticket.ticket_no || `#${ticket.id}`}
						size="small"
						color="primary"
						variant="outlined"
						sx={{ fontWeight: 700, ml: 0.5 }}
					/>
				</Box>
				<IconButton onClick={onDialogClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<Divider />

			<Box component="form" onSubmit={handleSubmit(onSubmit)}>
				<DialogContent sx={{ pt: 2, pb: 2 }}>
					{/* Visitor & Ticket Info Summary Banner */}
					<Paper
						variant="outlined"
						sx={{
							p: 1.8,
							mb: 2.5,
							borderRadius: 2,
							bgcolor: alpha(theme.palette.primary.main, 0.04),
							borderColor: alpha(theme.palette.primary.main, 0.15),
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							flexWrap: "wrap",
							gap: 1.5,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
							<PersonTwoToneIcon color="primary" fontSize="small" />
							<Box>
								<Typography variant="body2" fontWeight={700} color="text.primary">
									{ticket.visitor_name || "Visitor"}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{ticket.visitor_phone || ticket.visitor_email || "N/A"}
								</Typography>
							</Box>
						</Box>

						{ticket.visitor_no && (
							<Chip
								label={ticket.visitor_no}
								size="small"
								variant="filled"
								sx={{
									fontWeight: 700,
									borderRadius: 1.5,
									bgcolor: alpha(theme.palette.primary.main, 0.12),
									color: "primary.main",
								}}
							/>
						)}
					</Paper>

					<Grid container spacing={2}>
						{/* Visit Date Selection (2026-10-29, 2026-10-30, 2026-10-31) */}
						<Grid item xs={12}>
							<Box sx={{ mb: 0.5 }}>
								<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
									<Typography
										variant="h6"
										fontWeight={700}
										color="text.secondary"
										sx={{ fontSize: "0.75rem" }}
									>
										Select Visit Date <span style={{ color: "red" }}>*</span>
									</Typography>
									<Typography variant="caption" color="text.secondary">
										Selected: <strong style={{ color: theme.palette.primary.main }}>{watchedDate || "None"}</strong>
									</Typography>
								</Box>

								{isFairLoading ? (
									<Box sx={{ display: "flex", gap: 1, width: "100%", py: 0.5 }}>
										<Skeleton variant="circular" width="33%" height={38} sx={{ borderRadius: 1.25 }} />
										<Skeleton variant="circular" width="33%" height={38} sx={{ borderRadius: 1.25 }} />
										<Skeleton variant="circular" width="33%" height={38} sx={{ borderRadius: 1.25 }} />
									</Box>
								) : ticketDates.length > 0 ? (
									<Grid container spacing={1}>
										{ticketDates.map((item) => {
											const isSelected = watchedDate === item.value;
											return (
												<Grid item {...getGridXs(ticketDates.length)} key={item.value}>
													<Box
														onClick={() => {
															setValue("date", item.value, {
																shouldValidate: true,
																shouldDirty: true,
															});
														}}
														sx={{
															py: 0.75,
															px: { xs: 0.5, sm: 1 },
															borderRadius: 1.25,
															cursor: "pointer",
															userSelect: "none",
															border: isSelected
																? `1.5px solid ${theme.palette.primary.main}`
																: "1px solid rgba(0, 0, 0, 0.12)",
															bgcolor: isSelected
																? alpha(theme.palette.primary.main, 0.08)
																: "background.paper",
															boxShadow: isSelected
																? `0 1px 4px ${alpha(theme.palette.primary.main, 0.2)}`
																: "none",
															transition: "all 0.15s ease-in-out",
															display: "flex",
															flexDirection: { xs: "column", sm: "row" },
															alignItems: "center",
															justifyContent: "center",
															gap: { xs: 0.3, sm: 0.8 },
															minHeight: 38,
															"&:hover": {
																bgcolor: isSelected
																	? alpha(theme.palette.primary.main, 0.12)
																	: alpha(theme.palette.primary.main, 0.04),
																borderColor: theme.palette.primary.main,
															},
														}}
													>
														<Typography
															variant="caption"
															sx={{
																bgcolor: isSelected ? "primary.main" : alpha(theme.palette.primary.main, 0.1),
																color: isSelected ? "#ffffff" : "primary.main",
																px: 0.6,
																py: 0.1,
																borderRadius: 0.5,
																fontWeight: 700,
																fontSize: "0.68rem",
																lineHeight: 1.2,
																whiteSpace: "nowrap",
															}}
														>
															{item.day}
														</Typography>
														<Typography
															variant="body2"
															sx={{
																fontWeight: 700,
																fontSize: { xs: "0.75rem", sm: "0.82rem" },
																color: isSelected ? "primary.main" : "text.primary",
																lineHeight: 1.2,
																whiteSpace: "nowrap",
															}}
														>
															{item.label}
														</Typography>
													</Box>
												</Grid>
											);
										})}
									</Grid>
								) : (
									<Typography variant="caption" color="text.secondary">
										No fair dates available.
									</Typography>
								)}
								<input type="hidden" {...register("date", { required: true })} value={watchedDate} />
							</Box>
						</Grid>
						{/* Quantity */}
						<Grid item xs={12} sm={6}>
							<Typography
								variant="h6"
								fontWeight={700}
								color="text.secondary"
								sx={{ display: "block", mb: 0.6, fontSize: "0.75rem" }}
							>
								Ticket Quantity *
							</Typography>
							<TextField
								fullWidth
								size="small"
								type="number"
								placeholder="e.g. 1"
								inputProps={{ min: 1, step: "1" }}
								{...register("quantity", {
									required: "Quantity is required",
									min: {
										value: 1,
										message: "Quantity must be at least 1",
									},
								})}
								error={Boolean(errors.quantity)}
								helperText={errors.quantity?.message}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<ConfirmationNumberTwoToneIcon
												color="primary"
												fontSize="small"
											/>
										</InputAdornment>
									),
								}}
							/>
						</Grid>

						{/* Discount */}
						<Grid item xs={12} sm={6}>
							<Typography
								variant="h6"
								fontWeight={700}
								color="text.secondary"
								sx={{ display: "block", mb: 0.6, fontSize: "0.75rem" }}
							>
								Discount (BDT)
							</Typography>
							<TextField
								fullWidth
								size="small"
								type="number"
								placeholder="e.g. 0"
								inputProps={{ min: 0, step: "any" }}
								{...register("discount", {
									min: {
										value: 0,
										message: "Discount cannot be negative",
									},
								})}
								error={Boolean(errors.discount)}
								helperText={errors.discount?.message}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<PaidTwoToneIcon
												color="action"
												fontSize="small"
											/>
										</InputAdornment>
									),
									endAdornment: (
										<InputAdornment position="end">
											<Typography
												variant="caption"
												fontWeight={700}
												color="text.secondary"
											>
												BDT
											</Typography>
										</InputAdornment>
									),
								}}
							/>
						</Grid>

						{/* Payment Method */}
						<Grid item xs={12} sm={6}>
							<Typography
								variant="h6"
								fontWeight={700}
								color="text.secondary"
								sx={{ display: "block", mb: 0.6, fontSize: "0.75rem" }}
							>
								Payment Method *
							</Typography>
							<FormControl fullWidth size="small">
								<Controller
									name="payment_method"
									control={control}
									rules={{ required: "Payment method is required" }}
									render={({ field }) => (
										<Select
											{...field}
											startAdornment={
												<InputAdornment position="start">
													<PaymentTwoToneIcon
														color="primary"
														fontSize="small"
													/>
												</InputAdornment>
											}
										>
											{PAYMENT_METHODS.map((method) => (
												<MenuItem key={method.value} value={method.value}>
													{method.label}
												</MenuItem>
											))}
										</Select>
									)}
								/>
							</FormControl>
						</Grid>

						{/* Payment Status */}
						<Grid item xs={12} sm={6}>
							<Typography
								variant="h6"
								fontWeight={700}
								color="text.secondary"
								sx={{ display: "block", mb: 0.6, fontSize: "0.75rem" }}
							>
								Payment Status *
							</Typography>
							<FormControl fullWidth size="small">
								<Controller
									name="payment_status"
									control={control}
									rules={{ required: "Payment status is required" }}
									render={({ field }) => (
										<Select {...field}>
											{PAYMENT_STATUSES.map((status) => (
												<MenuItem key={status.value} value={status.value}>
													{status.label}
												</MenuItem>
											))}
										</Select>
									)}
								/>
							</FormControl>
						</Grid>

						{/* Payment Note / Transaction ID */}
						<Grid item xs={12}>
							<Typography
								variant="h6"
								fontWeight={700}
								color="text.secondary"
								sx={{ display: "block", mb: 0.6, fontSize: "0.75rem" }}
							>
								Payment Note / TRX ID
							</Typography>
							<TextField
								fullWidth
								size="small"
								placeholder="e.g. TRX987654321"
								{...register("note")}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<NoteAltTwoToneIcon
												color="action"
												fontSize="small"
											/>
										</InputAdornment>
									),
								}}
							/>
						</Grid>
					</Grid>
				</DialogContent>

				<Divider />

				<DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
					<Button
						onClick={onDialogClose}
						size="small"
						variant="outlined"
						color="inherit"
						disabled={isPending}
						sx={{
							borderRadius: 1.5,
							textTransform: "none",
							fontWeight: 600,
						}}
					>
						Cancel
					</Button>
					<LoadingButton
						type="submit"
						variant="contained"
						color="primary"
						size="small"
						loading={isPending}
						startIcon={<EditTwoToneIcon fontSize="small" />}
						sx={{
							borderRadius: 1.5,
							textTransform: "none",
							fontWeight: 700,
							px: 2.5,
							boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
						}}
					>
						<span>Update Ticket</span>
					</LoadingButton>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default UpdateTicketModal;
